import { EOIcon } from '@nx/ui/eo';
import { useI18n } from '@nx/i18n';
import {
  buildEventDays,
  detectConflicts,
  durationMinutes,
  type Activity,
  type ActivityType,
} from '@nx/domain';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  activitiesWatchRequested,
  activityCreateRequested,
  activityUpdateRequested,
} from '../../store/slices/activitiesSlice';
import { eventLoadRequested } from '../../store/slices/eventsSlice';
import { useEoToast } from '../../hooks/useEoToast';
import { disableEventPublicLink, enableEventPublicLink } from '../../services/eoCallables';
import {
  ActivityModal,
  ExportModal,
  ShareModal,
  TimelineView,
} from './agenda/AgendaModals';
import { activityTypeKind, activityTypeLabel } from './agenda/activityTypeKind';

export function AgendaPage() {
  const { t } = useI18n();
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { toast } = useEoToast();
  const event = useAppSelector((s) => s.events.current);
  const activities = useAppSelector((s) => s.activities.items);

  const days = useMemo(
    () => (event ? buildEventDays(event.startDate, event.endDate) : []),
    [event],
  );
  const [dayId, setDayId] = useState('d1');
  const [view, setView] = useState<'list' | 'timeline'>('list');
  const [modal, setModal] = useState<'new' | 'export' | 'share' | Activity | null>(null);
  const [publicLink, setPublicLink] = useState('');

  const selectedDay = days.find((d) => d.id === dayId) ?? days[0];
  const dayDate = selectedDay?.date ?? '';

  const dayActivities = useMemo(
    () =>
      activities
        .filter((a) => a.date === dayDate)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [activities, dayDate],
  );

  const conflictIds = useMemo(() => detectConflicts(dayActivities), [dayActivities]);

  useEffect(() => {
    if (eventId) {
      dispatch(eventLoadRequested(eventId));
      dispatch(activitiesWatchRequested(eventId));
    }
  }, [dispatch, eventId]);

  useEffect(() => {
    if (days[0] && !days.find((d) => d.id === dayId)) {
      setDayId(days[0].id);
    }
  }, [days, dayId]);

  useEffect(() => {
    if (event?.publicAgendaToken) {
      setPublicLink(`${window.location.origin}/p/${event.publicAgendaToken}`);
    }
  }, [event?.publicAgendaToken]);

  if (!event) {
    return <p className="muted-text">{t('common.loading')}</p>;
  }

  function saveActivity(input: {
    date: string;
    title: string;
    type: ActivityType;
    startTime: string;
    endTime: string;
    venue: string;
    sortOrder: number;
  }) {
    if (modal && typeof modal === 'object' && 'id' in modal) {
      dispatch(
        activityUpdateRequested({
          eventId,
          activityId: modal.id,
          input: {
            date: input.date,
            title: input.title,
            type: input.type,
            startTime: input.startTime,
            endTime: input.endTime,
            venue: input.venue,
          },
        }),
      );
    } else {
      dispatch(
        activityCreateRequested({
          eventId,
          input: {
            date: input.date,
            title: input.title,
            type: input.type,
            startTime: input.startTime,
            endTime: input.endTime,
            venue: input.venue,
            sortOrder: input.sortOrder,
          },
        }),
      );
    }
    setModal(null);
  }

  return (
    <>
      <p className="eo-bar-crumb">
        <button type="button" className="subtle-link" onClick={() => navigate('/eo/events')}>
          {t('eo.nav.events')}
        </button>
        <span className="sep">›</span>
        {event.title}
        <span className="sep">›</span>
        {t('eo.nav.agenda')}
      </p>

      <div className="page-head">
        <div>
          <h2>{event.title}</h2>
          <p>
            <EOIcon n="pin" size={12} /> {event.location} ·{' '}
            {event.startDate.toISOString().slice(0, 10)} — {event.endDate.toISOString().slice(0, 10)}
          </p>
        </div>
        <div className="page-head-actions">
          <button type="button" className="btn btn-outline" onClick={() => setModal('share')}>
            <EOIcon n="share" size={15} />
            {t('eo.agenda.share')}
          </button>
          <button type="button" className="btn btn-outline" onClick={() => setModal('export')}>
            <EOIcon n="dl" size={15} />
            {t('eo.agenda.export')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setModal('new')}>
            <EOIcon n="plus" size={15} />
            {t('eo.agenda.addActivity')}
          </button>
        </div>
      </div>

      {conflictIds.size > 0 ? (
        <div className="conflict-banner">
          <EOIcon n="alert" size={18} />
          <div>
            <strong>{t('eo.agenda.conflictsDetected')}</strong>
            <p>{t('eo.agenda.conflictsHint')}</p>
          </div>
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div className="day-tabs">
          {days.map((d) => (
            <button
              key={d.id}
              type="button"
              className={`day-tab ${dayId === d.id ? 'on' : ''}`}
              onClick={() => setDayId(d.id)}
            >
              <div>
                <div className="num">{d.label}</div>
                <div className="date">
                  {d.weekday} · {d.date.slice(5)}
                </div>
              </div>
            </button>
          ))}
        </div>
        <div className="seg">
          <button type="button" className={view === 'list' ? 'on' : ''} onClick={() => setView('list')}>
            <EOIcon n="list" size={14} />
            {t('eo.agenda.list')}
          </button>
          <button
            type="button"
            className={view === 'timeline' ? 'on' : ''}
            onClick={() => setView('timeline')}
          >
            <EOIcon n="clock" size={14} />
            {t('eo.agenda.timeline')}
          </button>
        </div>
      </div>

      {view === 'list' ? (
        <div className="activity-list">
          {dayActivities.map((a) => {
            const isConf = conflictIds.has(a.id);
            return (
              <div
                key={a.id}
                role="button"
                tabIndex={0}
                className={`activity-card ${isConf ? 'conflict' : ''}`}
                onClick={() => setModal(a)}
                onKeyDown={(e) => e.key === 'Enter' && setModal(a)}
              >
                <span className="drag" title="Drag to reorder">
                  <span className="d" />
                  <span className="d" />
                  <span className="d" />
                </span>
                <div className="time">
                  {a.startTime}
                  <small>↓ {a.endTime}</small>
                </div>
                <div className="title">
                  <strong>{a.title}</strong>
                  <div className="meta">
                    <span>
                      <EOIcon n="pin" size={12} /> {a.venue}
                    </span>
                    <span>
                      <EOIcon n="clock" size={12} /> {durationMinutes(a.startTime, a.endTime)} min
                    </span>
                  </div>
                </div>
                <div className="tags">
                  <span className={`badge ${activityTypeKind(a.type)}`}>
                    {activityTypeLabel(a.type)}
                  </span>
                  {isConf ? <span className="badge danger dot">Conflict</span> : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <TimelineView
          activities={dayActivities}
          conflictIds={conflictIds}
          onPick={(a) => setModal(a)}
        />
      )}

      {(modal === 'new' || (modal && typeof modal === 'object')) && (
        <ActivityModal
          open
          mode={modal === 'new' ? 'new' : 'edit'}
          event={event}
          days={days}
          dayId={dayId}
          initial={modal === 'new' ? undefined : modal}
          activities={activities}
          onClose={() => setModal(null)}
          onSave={saveActivity}
        />
      )}

      <ExportModal
        open={modal === 'export'}
        onClose={() => setModal(null)}
        onDone={() => {
          setModal(null);
          toast(t('eo.agenda.exportStarted'));
        }}
      />

      <ShareModal
        open={modal === 'share'}
        event={event}
        link={publicLink}
        onClose={() => setModal(null)}
        onPublicView={() => {
          if (event.publicAgendaToken) {
            window.open(`/p/${event.publicAgendaToken}`, '_blank');
          }
        }}
        onEnable={async () => {
          try {
            const res = await enableEventPublicLink(eventId);
            setPublicLink(res.url);
            dispatch(eventLoadRequested(eventId));
            toast(t('eo.agenda.linkGenerated'), 'success');
          } catch {
            toast(t('eo.agenda.linkFailed'), 'danger');
          }
        }}
        onDisable={async () => {
          try {
            await disableEventPublicLink(eventId);
            setPublicLink('');
            dispatch(eventLoadRequested(eventId));
            toast(t('eo.agenda.linkDisabled'), 'success');
          } catch {
            toast(t('eo.agenda.linkFailed'), 'danger');
          }
        }}
      />
    </>
  );
}
